// The dashboard grid's geometry (G001 Lane AR, DESIGN D6; DESIGN-R2 B4, B7, B10, B11, B14, B15) - PURE functions, no DOM,
// so they are unit-tested.
//
//   layoutPlane()  where each asset sits: a CSS grid with a left gutter column (the band labels) and one column per
//                  source column; a band holds one or more sub-rows so a tile spanning two columns never overlaps a
//                  single-column tile of the same band; header rows carry the column / segment labels; the rail row
//                  sits right after the rail's first band. A cell whose tile spans several bands is marked `tall` (the
//                  tile fills the whole spanned height); a cell spanning several columns is marked `wide` when one of
//                  those columns would otherwise show no tile in that band (the tiles grow to fill the span) - so every
//                  (column, band) cell the model covers shows a tile. The column guides break at each segment header
//                  whose label changes, so a relabelled column never reads as one lane. It decides WHERE an asset is
//                  drawn - never its state. An asset whose band or columns this plane does not define is returned as
//                  `unplaceable` (the page draws it in the unplaced / invalid strip - never dropped).
//   routeLinks()   the connectors: orthogonal paths found by a shortest-path search over a sparse grid of candidate
//                  tracks, from a point on the source tile's bottom edge to a point on the target tile's top edge,
//                  around every tile and every label (band labels, column / segment chips, the rail's labels). Each
//                  path that is routed reserves its strokes: a later path that is UNRELATED to it (a different source
//                  AND a different target, or a different kind) never runs on the same line within 8.5px of it, and
//                  never beside it closer than 6px (8px for a wire of the other kind - DESIGN-R3 C10) - so two
//                  unrelated wires never share a stroke, run as one thick line or read as one continuing line; the
//                  rail's own labels keep wires a few px further off (never framed by them). Stubs leave each source at
//                  their own x (per source and kind), lanes and crossbars are per path, and every path ends with a
//                  straight leg of at least LAST_LEG px (the arrowhead sits on it). Rail links drop onto their own rail
//                  track (flow above the rail bar, JDBC below it, one track per source), run along it and leave it for
//                  their target. `clear: false` says when no route clear of every other box was found (still drawn).
//   placeWireLabels() where each JDBC wire's label goes (DESIGN-R3 C13): beside its last leg, below the rail, clear of
//                  every other wire, box and label.
// G001 Lane AR fix round 4 (agent-26's visual verify of the R3 tree):
//   D3  no corner kiss: beside an unrelated / other-kind wire (1 < offset < SIDE / SIDE_KIND) a wire's end keeps KISS_GAP
//       px from the other's end along the line (CHAIN_GAP within CHAIN_OFF px); the last LAST_LEG px of every routed path
//       are reserved - no wire into another target crosses or touches them; a JDBC last leg is >= JDBC_LAST_LEG px with a
//       free label spot beside it, kept free for the label.
//   D4  wires keep TILE_PAD (6) px from every tile that is not their endpoint; the rail label keeps RAIL_LABEL_FREE_SIDES
//       free of wires (none within RAIL_LABEL_FREE px along them).
//   D5  each JDBC label sits beside its OWN last leg (never another leg); wires sharing their last leg get one label.
// G001 Lane AR fix round 5 (agent-28's visual verify of the R4 tree, orch/fix4/v0.json):
//   E1  no bundle split at a merge: two wires that are unrelated to each other never meet on the stroke of a third wire they
//       are both related to - where a wire joins (or leaves) a bundle, its shared stretch keeps NEAR px along that wire
//       from where another of the bundle's wires, unrelated to it, leaves (or joins) it (agent-20's N2: a false edge).
//   E2  no wire runs along a dashed column divider: the lanes sit LANE_OFF px beside each divider (never on it), and no
//       vertical run comes within GUIDE_SIDE px sideways of a divider (agent-28's GUIDE).
import type { HealthGridAsset, HealthGridLink } from "./types";

// ----------------------------------------------------------------------------------------------- layout --
export interface PlaneColumn {
  id: string;
  /** label segments: `fromBand` is the band index the label starts at (control-plane columns have one, from 0) */
  segments: { fromBand: number; label: string }[];
}

export interface PlaneBand {
  id: string;
  label: string;
}

export interface GridCell {
  key: string;
  /** CSS grid lines (1-based): column 1 is the band gutter, source column i is i + 2 */
  row: number;
  rowSpan: number;
  col: number;
  colSpan: number;
  assets: HealthGridAsset[];
  /** the tiles fill the spanned height (several bands) / grow to fill the spanned width (a column would be empty) */
  fill: { tall: boolean; wide: boolean };
}

export interface GridHeaderRow {
  row: number;
  /** true for the top row (the first segment of every column) */
  first: boolean;
  beforeBand: string;
  cells: { col: number; label: string; continued: boolean }[];
}

export interface GridBandRows {
  id: string;
  label: string;
  index: number;
  row: number;
  rowSpan: number;
}

export interface GridGuide {
  col: number;
  /** CSS grid row lines: from rowStart up to (not including) rowEnd */
  rowStart: number;
  rowEnd: number;
}

export interface PlaneLayout {
  columns: number;
  rowCount: number;
  bands: GridBandRows[];
  headers: GridHeaderRow[];
  rail: { row: number; col: number; colSpan: number } | null;
  cells: GridCell[];
  /** the column guides, broken at each segment header row where the column's label changes */
  guides: GridGuide[];
  unplaceable: { asset: HealthGridAsset; why: string }[];
}

interface Span {
  a: HealthGridAsset;
  order: number;
  b0: number;
  b1: number;
  c0: number;
  span: number;
}

const contiguous = (xs: number[]) => {
  const u = [...new Set(xs)].sort((x, y) => x - y);
  return u.length > 0 && u[u.length - 1] - u[0] + 1 === u.length ? { lo: u[0], hi: u[u.length - 1] } : null;
};
const overlap = (a0: number, a1: number, b0: number, b1: number) => a0 <= b1 && b0 <= a1;

/** The answer's dataflow columns as layout columns: each segment's `from_band` becomes a band index (a segment that
 *  names no band of the plane labels nothing - the column is still drawn). */
export function columnsFromGrid(bands: { id: string }[], cols: { id: string; segments?: { from_band: string; label: string }[] }[]): PlaneColumn[] {
  return cols.map(c => ({
    id: c.id,
    segments: (Array.isArray(c.segments) ? c.segments : [])
      .map(s => ({ fromBand: bands.findIndex(b => b.id === s.from_band), label: String(s.label ?? "") }))
      .filter(s => s.fromBand >= 0)
  }));
}

/** The control plane's columns: one label each, from the first band. */
export function columnsFromLabels(cols: { id: string; label?: string }[]): PlaneColumn[] {
  return cols.map(c => ({ id: c.id, segments: [{ fromBand: 0, label: String(c.label ?? c.id) }] }));
}

/** The segment of a column that is current at band index `i`. */
export function segmentAt(c: PlaneColumn, i: number): { fromBand: number; label: string } | null {
  let cur: { fromBand: number; label: string } | null = null;
  for (const s of c.segments) if (s.fromBand <= i && (cur === null || s.fromBand >= cur.fromBand)) cur = s;
  return cur;
}

export function layoutPlane(
  bands: PlaneBand[],
  columns: PlaneColumn[],
  assets: HealthGridAsset[],
  rail: { between: string[]; assets: string[] } | null = null
): PlaneLayout {
  const bIdx = new Map(bands.map((b, i) => [b.id, i] as const));
  const cIdx = new Map(columns.map((c, i) => [c.id, i] as const));
  const unplaceable: { asset: HealthGridAsset; why: string }[] = [];
  const spans: Span[] = [];
  assets.forEach((a, order) => {
    const bl = Array.isArray(a.bands) && a.bands.length > 0 ? a.bands : [a.band];
    const badBand = [a.band, ...bl].find(b => !bIdx.has(b));
    if (badBand !== undefined) return void unplaceable.push({ asset: a, why: `its band '${badBand}' is not a band of the ${a.plane} plane` });
    const bs = contiguous(bl.map(b => bIdx.get(b) as number));
    if (bs === null) return void unplaceable.push({ asset: a, why: "its bands are not adjacent" });
    if (!Array.isArray(a.cols) || a.cols.length === 0) return void unplaceable.push({ asset: a, why: "it names no column" });
    const badCol = a.cols.find(c => !cIdx.has(c));
    if (badCol !== undefined) return void unplaceable.push({ asset: a, why: `its column '${badCol}' is not a column of the ${a.plane} plane` });
    const cs = contiguous(a.cols.map(c => cIdx.get(c) as number));
    if (cs === null) return void unplaceable.push({ asset: a, why: "its columns are not adjacent" });
    spans.push({ a, order, b0: bs.lo, b1: bs.hi, c0: cs.lo, span: cs.hi - cs.lo + 1 });
  });
  // A tile spanning several bands keeps its span only when nothing else in those bands shares its columns;
  // otherwise it is drawn in its own `band` (a layout choice - nothing is hidden).
  for (const s of spans) {
    if (s.b1 === s.b0) continue;
    const clash = spans.some(t => t !== s && overlap(s.b0, s.b1, t.b0, t.b1) && overlap(s.c0, s.c0 + s.span - 1, t.c0, t.c0 + t.span - 1));
    if (clash) {
      const home = bIdx.get(s.a.band) as number;
      s.b0 = home;
      s.b1 = home;
    }
  }
  // group equal spans into one cell, in model order of first appearance
  const groups: { key: string; b0: number; b1: number; c0: number; span: number; first: number; assets: HealthGridAsset[] }[] = [];
  for (const s of spans) {
    const key = `${s.b0}:${s.b1}:${s.c0}:${s.span}`;
    let g = groups.find(x => x.key === key);
    if (!g) {
      g = { key, b0: s.b0, b1: s.b1, c0: s.c0, span: s.span, first: s.order, assets: [] };
      groups.push(g);
    }
    g.assets.push(s.a);
  }
  // sub-rows per band: the first sub-row where the group's columns are free
  const subOf = new Map<string, number>();
  const subRows = bands.map((_, i) => {
    const occ: Set<number>[] = [];
    for (const g of groups.filter(x => x.b0 === i && x.b1 === i).sort((x, y) => x.first - y.first)) {
      const cols = Array.from({ length: g.span }, (_, k) => g.c0 + k);
      let r = occ.findIndex(set => !cols.some(c => set.has(c)));
      if (r < 0) {
        occ.push(new Set());
        r = occ.length - 1;
      }
      for (const c of cols) occ[r].add(c);
      subOf.set(g.key, r);
    }
    return Math.max(1, occ.length);
  });
  // rows: the top header, then per band (a segment header before a band where a column's label changes), the rail row
  // right after the rail's first band
  const boundary = (i: number) => i > 0 && columns.some(c => c.segments.some(s => s.fromBand === i));
  const header = (i: number, first: boolean, row: number): GridHeaderRow => ({
    row,
    first,
    beforeBand: bands[i]?.id ?? "",
    cells: columns.map((c, k) => {
      const seg = segmentAt(c, i);
      return { col: k + 2, label: seg?.label ?? "", continued: !first && seg !== null && seg.fromBand < i };
    })
  });
  let row = 1;
  const headers: GridHeaderRow[] = [header(0, true, row++)];
  const bandRows: GridBandRows[] = [];
  let railRow: number | null = null;
  bands.forEach((b, i) => {
    if (boundary(i)) headers.push(header(i, false, row++));
    bandRows.push({ id: b.id, label: b.label, index: i, row, rowSpan: subRows[i] });
    row += subRows[i];
    if (rail && rail.between[0] === b.id && bIdx.has(rail.between[1] ?? "")) railRow = row++;
  });
  // B4: is (column c, band b) shown by a group other than g?
  const coveredByOther = (g: (typeof groups)[number], c: number, b: number) =>
    groups.some(o => o !== g && o.b0 <= b && b <= o.b1 && o.c0 <= c && c < o.c0 + o.span);
  const cells: GridCell[] = groups.map(g => {
    const top = bandRows[g.b0];
    const bottom = bandRows[g.b1];
    let wide = false;
    if (g.span > 1) {
      for (let c = g.c0; c < g.c0 + g.span && !wide; c++) for (let b = g.b0; b <= g.b1 && !wide; b++) if (!coveredByOther(g, c, b)) wide = true;
    }
    const fill = { tall: g.b1 > g.b0, wide };
    return g.b0 === g.b1
      ? { key: g.key, row: top.row + (subOf.get(g.key) ?? 0), rowSpan: 1, col: g.c0 + 2, colSpan: g.span, assets: g.assets, fill }
      : { key: g.key, row: top.row, rowSpan: bottom.row + bottom.rowSpan - top.row, col: g.c0 + 2, colSpan: g.span, assets: g.assets, fill };
  });
  let railCell: PlaneLayout["rail"] = null;
  if (rail && railRow !== null) {
    const cs = spans.filter(s => rail.assets.includes(s.a.id)).flatMap(s => [s.c0, s.c0 + s.span - 1]);
    const lo = cs.length > 0 ? Math.min(...cs) : 0;
    const hi = cs.length > 0 ? Math.max(...cs) : columns.length - 1;
    railCell = { row: railRow, col: lo + 2, colSpan: hi - lo + 1 };
  }
  // B7: each column's guide runs from the top to the last row, broken at every segment header row where that column's
  // label changes (a continued label keeps its guide unbroken)
  const lastLine = row;
  const guides: GridGuide[] = [];
  columns.forEach((_, k) => {
    const breaks = headers.filter(h => !h.first && h.cells[k] && !h.cells[k].continued).map(h => h.row);
    let start = 1;
    for (const b of breaks) {
      if (b > start) guides.push({ col: k + 2, rowStart: start, rowEnd: b });
      start = b + 1;
    }
    if (lastLine > start) guides.push({ col: k + 2, rowStart: start, rowEnd: lastLine });
  });
  return { columns: columns.length, rowCount: row - 1, bands: bandRows, headers, rail: railCell, cells, guides, unplaceable };
}

// ---------------------------------------------------------------------------------------------- routing --
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type Point = [number, number];

export interface RoutedPath {
  from: string;
  to: string;
  kind: "flow" | "jdbc";
  via: "rail" | null;
  points: Point[];
  /** false when no route clear of every other box was found (the path is still drawn, end to end) */
  clear: boolean;
  /** which RELAX level routed it: 0 = every rule held; higher = a rule had to be relaxed (the proof reports it) */
  relax?: number;
  /** R4 D5: a JDBC wire's label spot beside its LAST leg, kept free by the router (placeWireLabels starts there) */
  labelBox?: Rect | null;
}

export type Side = "top" | "right" | "bottom" | "left";

export interface RouteEnv {
  width: number;
  height: number;
  /** boxes that are not tiles but that no wire may cross: band labels, column / segment header chips, rail labels;
   *  `pad` keeps wires that much further off (the rail's labels: RAIL_LABEL_PAD); `free` names sides no wire may run
   *  along within RAIL_LABEL_FREE px (R4 D4: the rail label keeps two sides free - RAIL_LABEL_FREE_SIDES) */
  labels?: { id: string; rect: Rect; pad?: number; free?: readonly Side[] }[];
  /** the rail: its strip's box, and the y of its flow lane (above its bar) and of its JDBC lane (below it) */
  rail?: { box: Rect; flowY: number; jdbcY: number } | null;
  /** x positions of the vertical corridors (beside the column edges, where no tile sits - lanesFromHeads) */
  lanes: number[];
  /** R5 E2: the dashed column dividers - each line's centre x and its vertical extent; no wire runs along one within
   *  GUIDE_SIDE px */
  guides?: Guide[];
}

/** R5 E2: a dashed column divider as the page draws it (.ph-colguide, a 1px dashed border-left): its centre and extent */
export interface Guide {
  x: number;
  y0: number;
  y1: number;
}

/** keep a wire this far from any label box it does not start or end on */
export const PAD = 3;
/** R4 D4: keep a wire at least this far from any TILE that is not its source or target (a wire 3-4px outside a tile's
 *  border reads as part of its outline or as a link into it - agent-26's HUG: 0 < gap < 6px over more than 20px) */
export const TILE_PAD = 6;
/** spacing of parallel tracks */
export const STEP = 4;
/** every path ends with a straight leg at least this long - the arrowhead (6px) sits on it; it is reserved (R4 D3) */
export const LAST_LEG = 12;
/** an unrelated wire never runs on the same line within this distance of another (a shared stroke / false continuation) */
export const NEAR = 8.5;
/** DESIGN-R3 C10: an unrelated wire of the same kind never runs BESIDE another closer than SIDE px over any length (two
 *  strokes 2-4px apart read as one line), and a wire of the other kind (flow vs JDBC) never closer than SIDE_KIND px */
export const SIDE = 6;
export const SIDE_KIND = 8;
/** R4 D3: nor does it END beside another (1 < offset < SIDE / SIDE_KIND) within KISS_GAP px of the other's end along the
 *  line - two corners 2px apart read as one continuing line or as a crossing (agent-26's corner kiss) ... */
export const KISS_GAP = NEAR;
/** ... and within CHAIN_OFF px sideways the ends keep CHAIN_GAP px apart (agent-26's CHAIN: ends <= 20px apart, 1-3px
 *  sideways, read as one line changing into the other) */
export const CHAIN_OFF = 3;
export const CHAIN_GAP = 20.5;
/** R4 D3: the last LAST_LEG px of every routed path are reserved: no wire into another target crosses or touches them
 *  within this many px sideways (the 6px arrowhead reaches 3px each side of its line) */
export const ARROW_SIDE = 3.5;
/** the rail's tracks: one per source and kind, this far apart (>= SIDE_KIND: neighbouring tracks never read as one) */
export const RAIL_STEP = 8;
/** the rail's own labels keep wires this much further off than other boxes (DESIGN-R3 C10) */
export const RAIL_LABEL_PAD = 5;
/** R4 D4: the rail label keeps these sides free - no wire runs along them within RAIL_LABEL_FREE px (never framed) */
export const RAIL_LABEL_FREE = 16;
export const RAIL_LABEL_FREE_SIDES: readonly Side[] = ["left", "bottom"];
/** a JDBC wire's label ("JDBC", 10px bold): its box, and its text baseline below the box top */
export const LABEL_W = 30;
export const LABEL_H = 14;
export const LABEL_BASE = 10.5;
/** the label sits this far beside its wire */
export const LABEL_GAP = 4;
/** R4 D3 / D5: a JDBC wire's last leg is at least this long - LABEL_GAP below its turn, the label, 2px above the target -
 *  so its label fits beside it (never on a middle leg); the router also requires a free spot for it there */
export const JDBC_LAST_LEG = LABEL_GAP + LABEL_H + 2;
/** R5 E2: no vertical wire runs within this many px sideways of a dashed column divider, over any stretch of it (a wire on
 *  the divider replaces it by a solid line; a JDBC wire's dashes mix with the divider's) - the same as between two wires */
export const GUIDE_SIDE = SIDE;
/** R5 E2: a lane sits this far beside its column's divider (>= GUIDE_SIDE after the 0.5px rounding of the tracks) */
export const LANE_OFF = SIDE + 1;
/** R5 E1: how often a link is routed again, each time with one more bundle junction kept clear, before its rules relax */
export const BUNDLE_TRIES = 8;
/** R5 E1: the `to` of a junction kept clear for one link - no asset id (GRID_ID_PATTERN) has a '~', so it is no wire's target and blocks every wire */
const BUNDLE_SPLIT = "~bundle-split";

/** The vertical corridors of a plane from its column heads (each head's box, grid-relative): beside the first column's left
 *  edge, on both sides of every other column edge - never ON it, where the dashed divider is drawn (R5 E2) - and inside the
 *  last column's right edge. GridPlane measures the heads; grid.test.ts pins the captured geometry to this. */
export function lanesFromHeads(heads: Rect[]): number[] {
  const lanes: number[] = [];
  heads.forEach((r, k) => {
    if (k === 0) lanes.push(r.x + LANE_OFF);
    else lanes.push(r.x - LANE_OFF, r.x + LANE_OFF);
    if (k === heads.length - 1) lanes.push(r.x + r.w - 3);
  });
  return lanes;
}
/** the routing rules, strictest first: [end-to-end clearance on one line, kept apart sideways]; a link is routed by the
 *  first that finds a route, and the level it needed is recorded on the path (`relax`) */
const RELAX: readonly (readonly [number | null, boolean])[] = [[NEAR, true], [1.5, true], [NEAR, false], [1.5, false], [null, false]];
const BEND = 24;
const OFFS = [0, -6, 6, -12, 12, -18, 18, -24, 24, -30, 30];
const MAX_EXPAND = 150_000;

const r2 = (v: number) => Math.round(v * 2) / 2;

type Owner = { from: string; to: string; kind: "flow" | "jdbc" };
interface OccSeg {
  lo: number;
  hi: number;
  o: Owner;
}
/** Two wires may share a stroke only when they are the same kind AND share their source or their target. */
export const shareable = (a: Owner, b: Owner) => a.kind === b.kind && (a.from === b.from || a.to === b.to);

interface Ob extends Rect {
  id: string;
  /** how far wires keep off it: TILE_PAD for a tile, PAD for a label */
  pad: number;
}
interface Block {
  lo: number;
  hi: number;
  id: string;
}
/** R4 D3: a reserved stretch - the last LAST_LEG px of a routed path (`to`: wires into that same target may touch it, they
 *  share its arrowhead) or a JDBC label's spot (`to` null: no wire may touch it) */
interface Zone {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  to: string | null;
}

/** Does the axis-aligned segment p-q pass through the interior of r (grown by `pad`)? */
function hits(p: Point, q: Point, r: Rect, pad: number): boolean {
  const x0 = Math.min(p[0], q[0]);
  const x1 = Math.max(p[0], q[0]);
  const y0 = Math.min(p[1], q[1]);
  const y1 = Math.max(p[1], q[1]);
  return x0 < r.x + r.w + pad - 0.5 && x1 > r.x - pad + 0.5 && y0 < r.y + r.h + pad - 0.5 && y1 > r.y - pad + 0.5;
}

const uniq = (vs: number[], lo: number, hi: number) => [...new Set(vs.map(r2).filter(v => v >= lo && v <= hi))].sort((a, b) => a - b);

/** the x positions on a tile's edge a wire may leave or enter by: near its centre first */
const edgeXs = (r: Rect) => OFFS.map(d => r2(r.x + r.w / 2 + d)).filter(x => x >= r.x + 6 && x <= r.x + r.w - 6);

const segMeetsBox = (r: Rect, a: Point, b: Point, m: number) =>
  Math.min(a[0], b[0]) < r.x + r.w + m && Math.max(a[0], b[0]) > r.x - m && Math.min(a[1], b[1]) < r.y + r.h + m && Math.max(a[1], b[1]) > r.y - m;
const boxesMeet = (p: Rect, q: Rect, m: number) => p.x < q.x + q.w + m && q.x < p.x + p.w + m && p.y < q.y + q.h + m && q.y < p.y + p.h + m;

/** the reserved stretch of a path's last leg a -> b (b = the tip): its last LAST_LEG px, ARROW_SIDE px each side */
function lastLegZone(a: Point, b: Point): Omit<Zone, "to"> {
  if (a[0] === b[0]) {
    const down = b[1] >= a[1];
    return { x0: b[0] - ARROW_SIDE, x1: b[0] + ARROW_SIDE, y0: down ? Math.max(a[1], b[1] - LAST_LEG) : b[1], y1: down ? b[1] : Math.min(a[1], b[1] + LAST_LEG) };
  }
  const right = b[0] >= a[0];
  return { x0: right ? Math.max(a[0], b[0] - LAST_LEG) : b[0], x1: right ? b[0] : Math.min(a[0], b[0] + LAST_LEG), y0: b[1] - ARROW_SIDE, y1: b[1] + ARROW_SIDE };
}

class MinHeap {
  private f: number[] = [];
  private g: number[] = [];
  private s: number[] = [];
  get size() {
    return this.s.length;
  }
  private less(i: number, j: number) {
    return this.f[i] < this.f[j] || (this.f[i] === this.f[j] && this.g[i] > this.g[j]);
  }
  private swap(i: number, j: number) {
    [this.f[i], this.f[j]] = [this.f[j], this.f[i]];
    [this.g[i], this.g[j]] = [this.g[j], this.g[i]];
    [this.s[i], this.s[j]] = [this.s[j], this.s[i]];
  }
  push(f: number, g: number, s: number) {
    this.f.push(f);
    this.g.push(g);
    this.s.push(s);
    let i = this.s.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (!this.less(i, p)) break;
      this.swap(i, p);
      i = p;
    }
  }
  pop(): { f: number; g: number; s: number } {
    const top = { f: this.f[0], g: this.g[0], s: this.s[0] };
    const n = this.s.length - 1;
    this.swap(0, n);
    this.f.pop();
    this.g.pop();
    this.s.pop();
    let i = 0;
    for (;;) {
      const l = 2 * i + 1;
      const r = l + 1;
      let m = i;
      if (l < n && this.less(l, m)) m = l;
      if (r < n && this.less(r, m)) m = r;
      if (m === i) break;
      this.swap(i, m);
      i = m;
    }
    return top;
  }
}

/** The sparse routing grid: candidate x / y tracks, and for each track the stretches a box blocks. */
class Board {
  xs: number[];
  ys: number[];
  nx: number;
  ny: number;
  vb: Block[][];
  hb: Block[][];
  xi = new Map<number, number>();
  yi = new Map<number, number>();
  private validCache: Int8Array;
  occV = new Map<number, OccSeg[]>();
  occH = new Map<number, OccSeg[]>();
  /** R4 D3 / D5: the reserved last legs and label spots of the paths routed so far (R5 E1: and a link's kept-clear junctions) */
  zones: Zone[] = [];
  /** R5 E2: per x track, the y stretches where a dashed column divider runs within GUIDE_SIDE px of it */
  gb: Block[][];

  constructor(
    public obs: Ob[],
    xs: number[],
    ys: number[],
    public env: RouteEnv
  ) {
    this.xs = uniq(xs, 0.5, env.width - 0.5);
    this.ys = uniq(ys, 0.5, env.height - 0.5);
    this.nx = this.xs.length;
    this.ny = this.ys.length;
    this.xs.forEach((x, i) => this.xi.set(x, i));
    this.ys.forEach((y, i) => this.yi.set(y, i));
    this.vb = this.xs.map(x => obs.filter(o => o.x - o.pad < x && x < o.x + o.w + o.pad).map(o => ({ lo: o.y - o.pad, hi: o.y + o.h + o.pad, id: o.id })));
    this.hb = this.ys.map(y => obs.filter(o => o.y - o.pad < y && y < o.y + o.h + o.pad).map(o => ({ lo: o.x - o.pad, hi: o.x + o.w + o.pad, id: o.id })));
    this.validCache = new Int8Array(this.nx * this.ny);
    this.gb = this.xs.map(x => (env.guides ?? []).filter(g => Math.abs(x - g.x) < GUIDE_SIDE && g.y1 > g.y0).map(g => ({ lo: g.y0, hi: g.y1, id: "guide" })));
  }

  /** R5 E2: may a wire run down (or up) track i over (a, b)? Not beside a dashed column divider within GUIDE_SIDE px over
   *  any stretch of it - crossing a divider (a sideways move) is always fine */
  guideFree(i: number, a: number, b: number): boolean {
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    return this.gb[i].every(k => !(k.lo < hi && k.hi > lo));
  }

  valid(i: number, j: number): boolean {
    const k = i * this.ny + j;
    const c = this.validCache[k];
    if (c !== 0) return c === 1;
    const y = this.ys[j];
    const ok = !this.vb[i].some(b => b.lo < y && y < b.hi);
    this.validCache[k] = ok ? 1 : 2;
    return ok;
  }

  /** is the stretch (a, b) of track i clear of every box except `skip`? */
  clearV(i: number, a: number, b: number, skip?: string): boolean {
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    return this.vb[i].every(k => k.id === skip || !(k.lo < hi && k.hi > lo));
  }
  clearH(j: number, a: number, b: number, skip?: string): boolean {
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    return this.hb[j].every(k => k.id === skip || !(k.lo < hi && k.hi > lo));
  }

  /** may a wire of `o` run along the line at `c` over [a, b], given what is already routed? On (within 1px of) the line
   *  of an unrelated wire it stays `near` px clear of it end to end (no shared stroke, no false continuation); with
   *  `apart` it may also not run BESIDE one within SIDE px sideways (SIDE_KIND px for a wire of the other kind -
   *  DESIGN-R3 C10) - neither overlapping it nor (R4 D3) ending within KISS_GAP px of its end along the line (CHAIN_GAP
   *  within CHAIN_OFF px sideways): no corner kiss, no offset continuation. Related wires (same kind and the same source
   *  or target) may share and bundle. Relaxed (`apart` false) it keeps only the old 1.5px same-line rule. */
  occFree(map: Map<number, OccSeg[]>, c: number, a: number, b: number, o: Owner, near: number, apart: boolean): boolean {
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    const k = Math.round(c * 2);
    const reach = apart ? Math.round(SIDE_KIND * 2) - 1 : 3;
    const line = apart ? 1 : 1.5;
    for (let d = -reach; d <= reach; d++) {
      const segs = map.get(k + d);
      if (!segs) continue;
      const off = Math.abs(d) / 2;
      for (const s of segs) {
        if (shareable(s.o, o)) continue;
        if (off <= line) {
          if (s.lo - near < hi && s.hi + near > lo) return false;
        } else if (off < (s.o.kind !== o.kind ? SIDE_KIND : SIDE)) {
          const gap = off <= CHAIN_OFF ? CHAIN_GAP : KISS_GAP;
          if (s.lo - gap < hi && s.hi + gap > lo) return false;
        }
      }
    }
    return true;
  }

  reserve(pts: Point[], o: Owner) {
    for (let i = 1; i < pts.length; i++) {
      const [a, b] = [pts[i - 1], pts[i]];
      if (a[0] === b[0]) {
        const k = Math.round(a[0] * 2);
        this.occV.set(k, [...(this.occV.get(k) ?? []), { lo: Math.min(a[1], b[1]), hi: Math.max(a[1], b[1]), o }]);
      } else {
        const k = Math.round(a[1] * 2);
        this.occH.set(k, [...(this.occH.get(k) ?? []), { lo: Math.min(a[0], b[0]), hi: Math.max(a[0], b[0]), o }]);
      }
    }
  }

  /** R4 D3 / D5: does the segment p-q stay off every reserved zone a wire of `o` may not touch? */
  zoneFree(p: Point, q: Point, o: Owner): boolean {
    const x0 = Math.min(p[0], q[0]);
    const x1 = Math.max(p[0], q[0]);
    const y0 = Math.min(p[1], q[1]);
    const y1 = Math.max(p[1], q[1]);
    for (const z of this.zones) if (z.to !== o.to && x0 <= z.x1 && z.x0 <= x1 && y0 <= z.y1 && z.y0 <= y1) return false;
    return true;
  }

  /** does any routed wire (except those `exempt` says may) cross or touch the box [x0, x1] x [y0, y1]? */
  touched(x0: number, x1: number, y0: number, y1: number, exempt: ((s: Owner) => boolean) | null): boolean {
    for (let k = Math.floor(x0 * 2); k <= Math.ceil(x1 * 2); k++) {
      for (const s of this.occV.get(k) ?? []) if (!(exempt && exempt(s.o)) && s.lo <= y1 && s.hi >= y0) return true;
    }
    for (let k = Math.floor(y0 * 2); k <= Math.ceil(y1 * 2); k++) {
      for (const s of this.occH.get(k) ?? []) if (!(exempt && exempt(s.o)) && s.lo <= x1 && s.hi >= x0) return true;
    }
    return false;
  }

  /** R4 D3: the last LAST_LEG px of the leg a -> b (b the tip) is touched by no routed wire into another target */
  lastLegFree(a: Point, b: Point, o: Owner): boolean {
    const z = lastLegZone(a, b);
    return !this.touched(z.x0, z.x1, z.y0, z.y1, s => s.to === o.to);
  }

  /** R4 D5: may a JDBC label sit at r - inside the grid, off the rail strip, clear of every box, routed wire and label spot? */
  spotFree(r: Rect): boolean {
    const rail = this.env.rail ?? null;
    if (r.x < 0 || r.y < 0 || r.x + r.w > this.env.width || r.y + r.h > this.env.height) return false;
    if (rail && boxesMeet(r, rail.box, 1)) return false;
    if (this.obs.some(b => boxesMeet(r, b, 1))) return false;
    if (this.touched(r.x - 1.5, r.x + r.w + 1.5, r.y - 1.5, r.y + r.h + 1.5, null)) return false;
    return !this.zones.some(z => z.to === null && r.x - 2 <= z.x1 && z.x0 <= r.x + r.w + 2 && r.y - 2 <= z.y1 && z.y0 <= r.y + r.h + 2);
  }

  /** R4 D5: a free spot for a JDBC label beside the vertical last leg at x (from legTop down to the tip), below the rail,
   *  as near the tip as it fits, right side first; null when there is none */
  labelSpot(x: number, legTop: number, tip: number): Rect | null {
    const rail = this.env.rail ?? null;
    const top = Math.max(legTop + LABEL_GAP, rail ? rail.box.y + rail.box.h + 2 : -Infinity);
    for (let ty = r2(tip - 2 - LABEL_H); ty >= top; ty -= 1) {
      for (const bx of [x + LABEL_GAP, x - LABEL_GAP - LABEL_W]) {
        const r = { x: r2(bx), y: ty, w: LABEL_W, h: LABEL_H };
        if (this.spotFree(r)) return r;
      }
    }
    return null;
  }
}

interface Start {
  i: number;
  j: number;
  g: number;
  pre: Point[];
}
interface Goal {
  /** is (i, j) a goal? the extra cost to finish there, and the final point; null if not */
  at: (i: number, j: number) => { cost: number; end: Point | null } | null;
  h: (x: number, y: number) => number;
}
interface Rules {
  o: Owner;
  near: number | null;
  /** keep unrelated / other-kind wires SIDE / SIDE_KIND px apart sideways (DESIGN-R3 C10), ends apart (R4 D3) */
  apart: boolean;
  /** R4 D3 / D5: stay off every reserved last leg and label spot (dropped only at the last RELAX level) */
  zones: boolean;
  /** the cost factor of a sideways move at y (Infinity = not allowed there): a rail wire runs sideways in the rail row
   *  only on its own track, and elsewhere only to get round a label; any other wire never runs along the rail row, and
   *  prefers its crossbar nearest its source (DESIGN D6: "down to the gap below its band, across, down") */
  side: (y: number) => number;
}

function search(B: Board, starts: Start[], goal: Goal, R: Rules): Point[] | null {
  const ny = B.ny;
  const heap = new MinHeap();
  const best = new Map<number, number>();
  const prev = new Map<number, number>();
  const startPre = new Map<number, Point[]>();
  const sid = (i: number, j: number, d: number) => (i * ny + j) * 2 + d;
  for (const s of starts) {
    const id = sid(s.i, s.j, 0);
    if ((best.get(id) ?? Infinity) <= s.g) continue;
    best.set(id, s.g);
    startPre.set(id, s.pre);
    heap.push(s.g + goal.h(B.xs[s.i], B.ys[s.j]), s.g, id);
  }
  let expand = 0;
  while (heap.size > 0) {
    const cur = heap.pop();
    if (cur.g > (best.get(cur.s) ?? Infinity)) continue;
    const d = cur.s & 1;
    const node = cur.s >> 1;
    const i = Math.floor(node / ny);
    const j = node % ny;
    const fin = goal.at(i, j);
    if (fin !== null) {
      // rebuild
      const nodes: Point[] = [];
      let s = cur.s;
      for (;;) {
        const n = s >> 1;
        nodes.push([B.xs[Math.floor(n / ny)], B.ys[n % ny]]);
        const p = prev.get(s);
        if (p === undefined) break;
        s = p;
      }
      nodes.reverse();
      const pre = startPre.get(s) ?? [];
      return [...pre, ...nodes, ...(fin.end ? [fin.end] : [])];
    }
    if (++expand > MAX_EXPAND) return null;
    const x = B.xs[i];
    const y = B.ys[j];
    // vertical moves
    for (const dj of [-1, 1]) {
      const nj = j + dj;
      if (nj < 0 || nj >= ny || !B.valid(i, nj)) continue;
      const ny2 = B.ys[nj];
      if (!B.clearV(i, y, ny2)) continue;
      if (R.near !== null && !B.occFree(B.occV, x, y, ny2, R.o, R.near, R.apart)) continue;
      if (R.apart && !B.guideFree(i, y, ny2)) continue;
      if (R.zones && !B.zoneFree([x, y], [x, ny2], R.o)) continue;
      const g = cur.g + Math.abs(ny2 - y) + (d === 1 ? BEND : 0);
      const id = sid(i, nj, 0);
      if (g < (best.get(id) ?? Infinity)) {
        best.set(id, g);
        prev.set(id, cur.s);
        heap.push(g + goal.h(x, ny2), g, id);
      }
    }
    // sideways moves
    const factor = R.side(y);
    if (!Number.isFinite(factor)) continue;
    for (const di of [-1, 1]) {
      const ni = i + di;
      if (ni < 0 || ni >= B.nx || !B.valid(ni, j)) continue;
      const nx2 = B.xs[ni];
      if (!B.clearH(j, x, nx2)) continue;
      if (R.near !== null && !B.occFree(B.occH, y, x, nx2, R.o, R.near, R.apart)) continue;
      if (R.zones && !B.zoneFree([x, y], [nx2, y], R.o)) continue;
      const g = cur.g + Math.abs(nx2 - x) * factor + (d === 0 ? BEND : 0);
      const id = sid(ni, j, 1);
      if (g < (best.get(id) ?? Infinity)) {
        best.set(id, g);
        prev.set(id, cur.s);
        heap.push(g + goal.h(nx2, y), g, id);
      }
    }
  }
  return null;
}

/** collinear points dropped, repeated points dropped */
function simplify(pts: Point[]): Point[] {
  const p = pts.map(([x, y]) => [r2(x), r2(y)] as Point).filter((q, i, a) => i === 0 || q[0] !== a[i - 1][0] || q[1] !== a[i - 1][1]);
  const out: Point[] = [];
  for (const q of p) {
    while (out.length >= 2) {
      const [a, b] = [out[out.length - 2], out[out.length - 1]];
      if ((a[0] === b[0] && b[0] === q[0]) || (a[1] === b[1] && b[1] === q[1])) out.pop();
      else break;
    }
    out.push(q);
  }
  return out;
}

// ------------------------------------------------------------- R5 E1: no bundle split where an unrelated wire merges --
type ArcRange = [number, number];
interface RoutedLine {
  o: Owner;
  pts: Point[];
}

/** The stretches, as arc-length ranges along path a, where path b runs on a's stroke - collinear within 1px, over more than
 *  1px (agent-20's N2: prove-extra.py shared_intervals + arclen_intervals). */
function sharedRanges(a: Point[], b: Point[]): ArcRange[] {
  const out: ArcRange[] = [];
  let acc = 0;
  for (let i = 1; i < a.length; i++) {
    const [p, q] = [a[i - 1], a[i]];
    const len = Math.abs(p[0] - q[0]) + Math.abs(p[1] - q[1]);
    const v = p[0] === q[0];
    const [k, j] = v ? [0, 1] : [1, 0];
    for (let m = 1; m < b.length; m++) {
      const [c, d] = [b[m - 1], b[m]];
      if ((c[0] === d[0]) !== v || Math.abs(p[k] - c[k]) > 1) continue;
      const lo = Math.max(Math.min(p[j], q[j]), Math.min(c[j], d[j]));
      const hi = Math.min(Math.max(p[j], q[j]), Math.max(c[j], d[j]));
      if (hi - lo > 1) {
        const d1 = Math.abs(lo - p[j]);
        const d2 = Math.abs(hi - p[j]);
        out.push([acc + Math.min(d1, d2), acc + Math.max(d1, d2)]);
      }
    }
    acc += len;
  }
  return out;
}

/** the point at arc length s along a path */
function pointAt(a: Point[], s: number): Point {
  let acc = 0;
  for (let i = 1; i < a.length; i++) {
    const [p, q] = [a[i - 1], a[i]];
    const len = Math.abs(p[0] - q[0]) + Math.abs(p[1] - q[1]);
    if (s <= acc + len || i === a.length - 1) {
      const f = len > 0 ? Math.min(1, Math.max(0, (s - acc) / len)) : 0;
      return [p[0] + (q[0] - p[0]) * f, p[1] + (q[1] - p[1]) * f];
    }
    acc += len;
  }
  return a[0];
}

/** R5 E1: where the path x (just routed) would make two wires that are UNRELATED to each other (!shareable) meet - within
 *  NEAR px along it - on the stroke of a third wire they both share: x carrying both, or x joining (leaving) a wire A where
 *  another of A's wires, unrelated to x, leaves (joins) it (a bundle splitting at a merge reads as a false edge). Each such
 *  junction as the box around the two facing ends of the shared stretches, grown by NEAR (+0.5): a route kept out of the box
 *  keeps its shared stretch more than NEAR px along the wire from the other's. */
function bundleSplits(x: RoutedLine, routed: readonly RoutedLine[]): Rect[] {
  const out: Rect[] = [];
  const meet = (A: RoutedLine, P: RoutedLine, Q: RoutedLine) => {
    const ip = sharedRanges(A.pts, P.pts);
    if (ip.length === 0) return;
    const iq = sharedRanges(A.pts, Q.pts);
    for (const [p0, p1] of ip)
      for (const [q0, q1] of iq) {
        if (!(p0 < q1 + NEAR && q0 < p1 + NEAR)) continue;
        const [e, f] = p1 <= q0 ? [p1, q0] : q1 <= p0 ? [q1, p0] : [Math.max(p0, q0), Math.min(p1, q1)];
        const [u, w] = [pointAt(A.pts, e), pointAt(A.pts, f)];
        const m = NEAR + 0.5;
        out.push({ x: Math.min(u[0], w[0]) - m, y: Math.min(u[1], w[1]) - m, w: Math.abs(u[0] - w[0]) + 2 * m, h: Math.abs(u[1] - w[1]) + 2 * m });
      }
  };
  const mates = routed.filter(r => shareable(r.o, x.o));
  for (let i = 0; i < mates.length; i++)
    for (let j = i + 1; j < mates.length; j++) if (!shareable(mates[i].o, mates[j].o)) meet(x, mates[i], mates[j]);
  for (const A of mates) for (const C of routed) if (C !== A && shareable(C.o, A.o) && !shareable(C.o, x.o)) meet(A, x, C);
  return out;
}

/** Route every link whose two tiles were measured. Links to a tile that is not drawn (a hidden retired asset) are
 *  skipped - there is nothing to connect. */
export function routeLinks(links: HealthGridLink[], rects: Map<string, Rect>, env: RouteEnv): RoutedPath[] {
  // nothing to connect in this grid (the control plane): no routing board is built
  if (!links.some(l => rects.has(l.from) && rects.has(l.to) && l.from !== l.to)) return [];
  // R4 D4: tiles keep wires TILE_PAD off (a wire's own source and target excepted - the search skips them)
  const tiles: Ob[] = [...rects.entries()].map(([id, r]) => ({ id, ...r, pad: TILE_PAD }));
  // a label's own margin: the rail's labels keep wires further off (DESIGN-R3 C10), and a `free` side keeps every wire more
  // than RAIL_LABEL_FREE px off (R4 D4: never framed)
  const labels: Ob[] = (env.labels ?? []).map(l => {
    const m = l.pad ?? 0;
    const free = new Set(l.free ?? []);
    const g = (s: Side) => (free.has(s) ? Math.max(m, RAIL_LABEL_FREE + 1 - PAD) : m);
    return { id: l.id, x: l.rect.x - g("left"), y: l.rect.y - g("top"), w: l.rect.w + g("left") + g("right"), h: l.rect.h + g("top") + g("bottom"), pad: PAD };
  });
  const obs = [...tiles, ...labels];
  const rail = env.rail ?? null;
  // candidate tracks
  const xs: number[] = [];
  const ys: number[] = [];
  for (const t of tiles) xs.push(...edgeXs(t));
  for (const o of obs) {
    for (let k = 0; k < 4; k++) {
      xs.push(o.x - o.pad - 1 - STEP * k, o.x + o.w + o.pad + 1 + STEP * k);
      ys.push(o.y - o.pad - 1 - STEP * k, o.y - LAST_LEG - STEP * k, o.y - JDBC_LAST_LEG - STEP * k, o.y + o.h + o.pad + 1 + STEP * k);
    }
    ys.push(o.y + o.h + o.pad + 1 + STEP * 4);
  }
  for (const l of env.lanes) for (let k = -3; k <= 3; k++) xs.push(l + STEP * k);
  // rail tracks, one per source and kind: flow above the bar, JDBC below it
  const railLinks = rail ? links.filter(l => l.via === "rail" && rects.has(l.from) && rects.has(l.to)) : [];
  const trackOf = new Map<string, number>();
  if (rail) {
    for (const kind of ["flow", "jdbc"] as const) {
      const srcs = [...new Set(railLinks.filter(l => (l.kind === "jdbc" ? "jdbc" : "flow") === kind).map(l => l.from))].sort(
        (a, b) => (rects.get(a)?.x ?? 0) - (rects.get(b)?.x ?? 0)
      );
      srcs.forEach((s, k) => {
        const y = r2(kind === "flow" ? rail.flowY - RAIL_STEP * k : rail.jdbcY + RAIL_STEP * k);
        trackOf.set(`${s}|${kind}`, y);
        ys.push(y);
      });
    }
  }
  const B = new Board(obs, xs, ys, env);
  const railBand: [number, number] | null = rail ? [rail.box.y, rail.box.y + rail.box.h] : null;
  // preferred stub / entry per source (and per target), so one source's wires of one kind leave together
  const exitOf = new Map<string, number>();
  const entryOf = new Map<string, number>();

  const order = links
    .map((l, idx) => ({ l, idx }))
    .filter(({ l }) => rects.has(l.from) && rects.has(l.to) && l.from !== l.to)
    .sort((a, b) => {
      const ra = a.l.via === "rail" && rail ? 0 : 1;
      const rb = b.l.via === "rail" && rail ? 0 : 1;
      if (ra !== rb) return ra - rb;
      const da = dist(rects.get(a.l.from) as Rect, rects.get(a.l.to) as Rect);
      const db = dist(rects.get(b.l.from) as Rect, rects.get(b.l.to) as Rect);
      return da - db || a.idx - b.idx;
    });
  const out = new Map<number, RoutedPath>();
  /** R5 E1: the wires routed so far (the bundle-split rule reads them) */
  const routed: RoutedLine[] = [];
  for (const { l, idx } of order) {
    const S = rects.get(l.from) as Rect;
    const T = rects.get(l.to) as Rect;
    const kind: "flow" | "jdbc" = l.kind === "jdbc" ? "jdbc" : "flow";
    const o: Owner = { from: l.from, to: l.to, kind };
    const via = l.via === "rail" ? "rail" : null;
    const track = via && rail ? (trackOf.get(`${l.from}|${kind}`) ?? null) : null;
    let pts: Point[] | null = null;
    let clear = true;
    let relax = RELAX.length;
    // strictest first: unrelated wires kept apart sideways and end to end; relaxed only when no such route exists
    for (let lv = 0; lv < RELAX.length && !pts; lv++) {
      const [near, apart] = RELAX[lv];
      // R5 E1: a route that makes two wires unrelated to each other meet on a third's stroke is refused - that junction is
      // kept clear for this link and it is routed again (BUNDLE_TRIES times at most, then the next level)
      const held: Zone[] = [];
      for (let t = 0; t <= BUNDLE_TRIES && !pts; t++) {
        const got = routeOne(B, l.from, l.to, S, T, o, near, apart, railBand, track, rail, exitOf, entryOf);
        if (!got) break;
        const splits = near === null ? [] : bundleSplits({ o, pts: simplify(got) }, routed);
        if (splits.length === 0) pts = got;
        else
          for (const r of splits) {
            const z: Zone = { x0: r.x, x1: r.x + r.w, y0: r.y, y1: r.y + r.h, to: BUNDLE_SPLIT };
            B.zones.push(z);
            held.push(z);
          }
      }
      if (held.length > 0) B.zones = B.zones.filter(z => !held.includes(z));
      if (pts) relax = lv;
    }
    if (!pts) {
      // nothing clear: still drawn, end to end, and marked
      const sx = S.x + S.w / 2;
      const tx = T.x + T.w / 2;
      const ym = T.y >= S.y + S.h ? (S.y + S.h + T.y) / 2 : Math.min(env.height, S.y + S.h + 8);
      pts = [[sx, S.y + S.h], [sx, ym], [tx, ym], [tx, T.y]];
      clear = false;
    }
    const sp = simplify(pts);
    if (clear) {
      exitOf.set(`${l.from}|${kind}`, sp[0][0]);
      entryOf.set(`${l.to}|${kind}`, sp[sp.length - 1][0]);
    }
    B.reserve(sp, o);
    routed.push({ o, pts: sp });
    // R4 D3: its last leg is reserved against every wire into another target; D5: a JDBC wire's label spot beside it too
    let labelBox: Rect | null = null;
    if (clear && sp.length >= 2) {
      const [a, b] = sp.slice(-2);
      B.zones.push({ ...lastLegZone(a, b), to: l.to });
      if (kind === "jdbc" && a[0] === b[0]) {
        labelBox = B.labelSpot(b[0], Math.min(a[1], b[1]), Math.max(a[1], b[1]));
        if (labelBox) B.zones.push({ x0: labelBox.x - 2, x1: labelBox.x + labelBox.w + 2, y0: labelBox.y - 2, y1: labelBox.y + labelBox.h + 2, to: null });
      }
    }
    out.set(idx, { from: l.from, to: l.to, kind, via, points: sp, clear, relax, ...(kind === "jdbc" ? { labelBox } : {}) });
  }
  return [...out.entries()].sort((a, b) => a[0] - b[0]).map(([, p]) => p);
}

const dist = (S: Rect, T: Rect) => Math.abs(S.x + S.w / 2 - (T.x + T.w / 2)) + Math.abs(T.y - (S.y + S.h));

function routeOne(
  B: Board,
  from: string,
  to: string,
  S: Rect,
  T: Rect,
  o: Owner,
  near: number | null,
  apart: boolean,
  railBand: [number, number] | null,
  track: number | null,
  rail: RouteEnv["rail"] | null,
  exitOf: Map<string, number>,
  entryOf: Map<string, number>
): Point[] | null {
  const inRail = (y: number) => railBand !== null && y > railBand[0] && y < railBand[1];
  const yRef = S.y + S.h;
  const zones = near !== null;
  const R: Rules = {
    o,
    near,
    apart,
    zones,
    side:
      track !== null
        ? (y: number) => (inRail(y) ? (Math.abs(y - track) < 0.26 ? 1 : Infinity) : 4)
        : (y: number) => (inRail(y) ? Infinity : 1 + 0.0002 * Math.abs(y - yRef))
  };
  // side by side on one line, facing edges far enough apart for the arrow: straight across (never for a JDBC wire - its
  // label needs a vertical last leg)
  const shareTop = Math.max(S.y, T.y);
  const shareBot = Math.min(S.y + S.h, T.y + T.h);
  if (track === null && o.kind !== "jdbc" && shareBot - shareTop > 8) {
    const y = r2((shareTop + shareBot) / 2);
    const right = T.x >= S.x + S.w + LAST_LEG;
    const left = T.x + T.w + LAST_LEG <= S.x;
    if (right || left) {
      const p: Point = right ? [S.x + S.w, y] : [S.x, y];
      const q: Point = right ? [T.x, y] : [T.x + T.w, y];
      const clearBoxes = B.obs.every(ob => ob.id === from || ob.id === to || !hits(p, q, ob, ob.pad));
      const free = near === null || (B.occFree(B.occH, y, p[0], q[0], o, near, apart) && B.zoneFree(p, q, o) && B.lastLegFree(p, q, o));
      if (clearBoxes && free) return [p, q];
    }
  }
  const sBottom = S.y + S.h;
  const cS = S.x + S.w / 2;
  const cT = T.x + T.w / 2;
  const prefExit = exitOf.get(`${from}|${o.kind}`);
  const prefEntry = entryOf.get(`${to}|${o.kind}`);
  const endPenalty = (x: number, c: number, pref: number | undefined) => (pref !== undefined ? (Math.abs(x - pref) < 0.26 ? 0 : 40 + Math.abs(x - c)) : Math.abs(x - c) * 0.8);
  // starts: a stub straight down from the source's bottom edge to the first track below its keep-off
  const j0 = B.ys.findIndex(y => y >= sBottom + TILE_PAD + 0.5);
  const starts: Start[] = [];
  if (j0 >= 0) {
    for (const ex of edgeXs(S)) {
      const i = B.xi.get(ex);
      if (i === undefined || !B.valid(i, j0)) continue;
      if (!B.clearV(i, sBottom, B.ys[j0], from)) continue;
      if (near !== null && !B.occFree(B.occV, ex, sBottom, B.ys[j0], o, near, apart)) continue;
      if (apart && !B.guideFree(i, sBottom, B.ys[j0])) continue;
      if (zones && !B.zoneFree([ex, sBottom], [ex, B.ys[j0]], o)) continue;
      starts.push({ i, j: j0, g: B.ys[j0] - sBottom + endPenalty(ex, cS, prefExit), pre: [[ex, sBottom]] });
    }
  }
  if (starts.length === 0) return null;
  const entries = edgeXs(T);
  const entryIdx = new Map<number, number>();
  for (const ex of entries) {
    const i = B.xi.get(ex);
    if (i !== undefined) entryIdx.set(i, ex);
  }
  // R4 D3 / D5: a JDBC wire's last leg is long enough for its label, and a free spot for the label must exist beside it
  const top = T.y - (o.kind === "jdbc" ? JDBC_LAST_LEG : LAST_LEG);
  const toTarget: Goal = {
    at: (i, j) => {
      const ex = entryIdx.get(i);
      if (ex === undefined) return null;
      const y = B.ys[j];
      if (y > top + 0.01) return null;
      if (!B.clearV(i, y, T.y, to)) return null;
      if (near !== null && !B.occFree(B.occV, ex, y, T.y, o, near, apart)) return null;
      if (apart && !B.guideFree(i, y, T.y)) return null;
      if (zones && (!B.zoneFree([ex, y], [ex, T.y], o) || !B.lastLegFree([ex, y], [ex, T.y], o))) return null;
      if (zones && o.kind === "jdbc" && B.labelSpot(ex, y, T.y) === null) return null;
      return { cost: T.y - y + endPenalty(ex, cT, prefEntry), end: [ex, T.y] };
    },
    h: (x, y) => {
      let dx = Infinity;
      for (const e of entries) dx = Math.min(dx, Math.abs(x - e));
      return dx + Math.abs(y - top);
    }
  };
  if (track !== null && rail) {
    // phase 1: from the source onto its own rail track; phase 2: along it and on to the target
    const x0 = rail.box.x;
    const x1 = rail.box.x + rail.box.w;
    const onTrack: Goal = {
      at: (i, j) => (Math.abs(B.ys[j] - track) < 0.26 && B.xs[i] >= x0 && B.xs[i] <= x1 ? { cost: 0, end: null } : null),
      h: (x, y) => Math.abs(y - track) + (x < x0 ? x0 - x : x > x1 ? x - x1 : 0)
    };
    const p1 = search(B, starts, onTrack, R);
    if (!p1) return null;
    const last = p1[p1.length - 1];
    const i = B.xi.get(last[0]);
    const j = B.yi.get(last[1]);
    if (i === undefined || j === undefined) return null;
    const p2 = search(B, [{ i, j, g: 0, pre: [] }], toTarget, R);
    return p2 ? [...p1.slice(0, -1), ...p2] : null;
  }
  return search(B, starts, toTarget, R);
}

// ------------------------------------------------------------------------------------------ wire labels --
export interface WireLabel {
  /** the index of the labelled path in the `paths` given */
  path: number;
  /** R4 D5: the other JDBC paths that share this path's last leg (same target, same x, same tip) - one label for all */
  also: number[];
  box: Rect;
  /** which side of the last leg the label sits on (the page anchors its text at the leg side of the box) */
  side: "left" | "right";
  /** false when no spot clear of every other wire, box, label and the rail was found (still drawn, beside its last leg) */
  clear: boolean;
}

/** DESIGN-R3 C13 / R4 D5: where each JDBC wire's label goes - beside its LAST leg (never another leg), on the part below
 *  the rail (never on the rail strip or its bar), as near its target as it fits, on whichever side is clear of every wire
 *  (its own other legs included), every tile and label box and every label already placed; the spot the router kept free
 *  for it (RoutedPath.labelBox) first. JDBC wires that share their whole last leg get ONE label. A label with no clear spot
 *  is still drawn beside its last leg and marked `clear: false` (the proof fails on it). */
export function placeWireLabels(paths: RoutedPath[], rects: Map<string, Rect>, env: RouteEnv): WireLabel[] {
  const boxes: Rect[] = [...rects.values(), ...(env.labels ?? []).map(l => l.rect)];
  const rail = env.rail ?? null;
  const railBottom = rail ? rail.box.y + rail.box.h : -Infinity;
  const placed: Rect[] = [];
  const out: WireLabel[] = [];
  const fits = (r: Rect) =>
    r.x >= 0 && r.y >= 0 && r.x + r.w <= env.width && r.y + r.h <= env.height &&
    !(rail !== null && boxesMeet(r, rail.box, 1)) &&
    !boxes.some(b => boxesMeet(r, b, 1)) &&
    !placed.some(b => boxesMeet(r, b, 2)) &&
    !paths.some(q => q.points.some((pt, i) => i > 0 && segMeetsBox(r, q.points[i - 1], pt, 1.5)));
  const lastLeg = (p: RoutedPath): [Point, Point] => [p.points[p.points.length - 2], p.points[p.points.length - 1]];
  paths.forEach((p, idx) => {
    if (p.kind !== "jdbc" || p.points.length < 2) return;
    const [a, b] = lastLeg(p);
    const vertical = a[0] === b[0] && a[1] !== b[1];
    // one label for wires sharing their last leg: the same target, entered at the same x, the same tip
    const host = vertical ? out.find(l => {
      const q = paths[l.path];
      const [c, d] = lastLeg(q);
      return q.to === p.to && c[0] === d[0] && Math.abs(d[0] - b[0]) < 0.3 && Math.abs(d[1] - b[1]) < 0.3;
    }) : undefined;
    if (host) {
      host.also.push(idx);
      return;
    }
    let found: Rect | null = null;
    if (vertical) {
      const x = a[0];
      const cands: Rect[] = p.labelBox ? [p.labelBox] : [];
      const top = Math.max(Math.min(a[1], b[1]) + LABEL_GAP, railBottom + 2);
      const bottom = Math.max(a[1], b[1]) - 2;
      for (let ty = r2(bottom - LABEL_H); ty >= top; ty -= 1) {
        for (const bx of [x + LABEL_GAP, x - LABEL_GAP - LABEL_W]) cands.push({ x: r2(bx), y: ty, w: LABEL_W, h: LABEL_H });
      }
      found = cands.find(r => (Math.abs(r.x - (x + LABEL_GAP)) < 0.3 || Math.abs(r.x + r.w - (x - LABEL_GAP)) < 0.3) && fits(r)) ?? null;
    }
    const box = found ?? { x: r2(Math.max(a[0], b[0]) + LABEL_GAP), y: r2((a[1] + b[1]) / 2 - LABEL_H / 2), w: LABEL_W, h: LABEL_H };
    placed.push(box);
    out.push({ path: idx, also: [], box, side: box.x < a[0] ? "left" : "right", clear: found !== null });
  });
  return out;
}
